import {isOptions} from 'quickstart/lib/search/config'
import {runHook} from 'quickstart/lib/search/hooks'
import searchParams from 'quickstart/lib/search/search-params'
import * as R from 'rambdax'
import {useEffect, useMemo, useState} from 'react'
import {logger, shallowIdentical, sumPairs} from 'tizra'
import {useApi} from './useApi'
import {useEqualBy} from './useEqualBy'

const log = logger('quickstart/hooks/useHitCounts')

/**
 * React hook for fetching hit counts for a given field.
 *
 * @param {Object} search - result of useSearch
 * @param {Object} field - a field from config.fields
 * @param {Boolean} visible - is this field visible?
 */
export const useHitCounts = ({search, visible = true, ...props}) => {
  const picked = useEqualBy(shallowIdentical, {
    ...R.pick(['configured', 'config', 'depths', 'params'], search),
    ...props,
  })
  const [state, setState] = useState()

  useEffect(() => {
    if (visible && state !== picked) {
      setState(picked)
    }
  }, [picked, state, visible])

  const {configured, config, depths, params, field = {}} = state || {}
  const wantHits = field.hits || (field.hits !== false && field.prop)

  const sps = useMemo(
    () =>
      wantHits && configured && params
        ? fieldDepthSps({config, depths, field, params})
        : [],
    [config, configured, depths, field, params, wantHits],
  )

  // Make sure to call the hook all four times regardless, following the rules
  // of hooks. (Why four? See useSearch.)
  const queries = [
    useApi.propValues(sps[0]),
    useApi.propValues(sps[1]),
    useApi.propValues(sps[2]),
    useApi.propValues(sps[3]),
  ].slice(0, sps.length)

  const datas = useEqualBy(
    shallowIdentical,
    queries.map(R.prop('data')).filter(Boolean),
  )

  return useMemo(
    () => (wantHits && datas.length ? sumHitCounts({field, datas}) : null),
    [field, datas, wantHits],
  )
}

function fieldDepthSps({config, depths, field, params}) {
  if (!isOptions(field.options)) {
    return []
  }

  let sps = depths
    .map(depth =>
      searchParams(
        config,
        {
          ...params,
          // Clear this field in the prop-values search parameters,
          // since we want to know what the counts WOULD BE if any
          // values on this field were set, rather than WHEN values on
          // this field are set.
          [field.name]: field.multi ? [] : '',
          depth,
        },
        {
          allowEmpty: true,
          // field.api.contribute can use this to customize the query for
          // efficiency.
          hittingOn: field.name,
          logName: `${config.mode}.useHitCounts.${field.name}.${depth}`,
        },
      ),
    )
    .filter(Boolean)

  sps = sps.map(sp => ({
    ...sp,
    'prop-names': [field.prop],
    // full-info=true returns [{value: 'x', count: 4}, ...]
    // instead of bare ['x', ...]
    'full-info': true,
  }))

  return sps
}

function sumHitCounts({field, datas}) {
  const unsummed = datas.flatMap(d => d?.[field.prop]).filter(Boolean)
  // => [{value: 'a', count: 1}, {value: 'a', count: 2}]

  let summed = sumPairs(unsummed.map(({value, count}) => [value, count]))
  // => [{value: 'a', count: 3}]

  const hook = field.hooks?.hits
  if (hook) {
    summed = R.piped(
      summed,
      // Repackage {v: c, v: c} -> [{v, c}, {v, c}]
      R.toPairs,
      R.map(([value, count]) => ({value, count})),
      // Run the hook on the repackaged hit counts
      hits => runHook(hook, {field, hits}),
      // Reverse the repackaging
      R.map(({value, count}) => [value, count]),
      R.fromPairs,
    )
  }

  return summed
}
